import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/lib/supabase";

interface Story {
  id: string;
  slug: string;
  title_en: string;
  title_ps: string;
  title_da: string;
  excerpt_en: string;
  excerpt_ps: string;
  excerpt_da: string;
  content_en: string;
  content_ps: string;
  content_da: string;
  author_en: string;
  author_ps: string;
  author_da: string;
  author_role_en: string;
  author_role_ps: string;
  author_role_da: string;
  image: string;
  category: string;
  read_time_en: string;
  read_time_ps: string;
  read_time_da: string;
  created_at: string;
}

const categoryKeyMap: Record<string, string> = {
  All: "storiesPage.filters.all",
  "Field Stories": "storiesPage.filters.fieldStories",
  "Impact Reports": "storiesPage.filters.impactReports",
  "Volunteer Voices": "storiesPage.filters.volunteerVoices",
  "News & Updates": "storiesPage.filters.news",
};

const categoryColorMap: Record<string, string> = {
  "Field Stories": "bg-emerald-100 text-emerald-700",
  "Impact Reports": "bg-amber-100 text-amber-700",
  "Volunteer Voices": "bg-rose-100 text-rose-700",
  "News & Updates": "bg-sky-100 text-sky-700",
};

function getLocalizedField(story: Story, field: string, lang: string): string {
  const suffix = lang === "ps" ? "_ps" : lang === "da" ? "_da" : "_en";
  return (story as any)[field + suffix] || (story as any)[field + "_en"] || "";
}

export default function StoryDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();

  const [story, setStory] = useState<Story | null>(null);
  const [relatedStories, setRelatedStories] = useState<Story[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStory = useCallback(() => {
    if (!slug) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setNotFound(false);

    supabase
      .from("stories")
      .select("*")
      .eq("slug", slug)
      .eq("published", true)
      .maybeSingle()
      .then(({ data, error: err }) => {
        if (err) {
          console.error("Story detail fetch error:", err.message);
          setError(err.message);
          setLoading(false);
          return;
        }
        if (!data) {
          setNotFound(true);
          setLoading(false);
          return;
        }
        const s = data as Story;
        setStory(s);
        // Fetch related stories
        supabase
          .from("stories")
          .select("*")
          .eq("published", true)
          .eq("category", s.category)
          .neq("id", s.id)
          .limit(3)
          .order("created_at", { ascending: false })
          .then(({ data: related, error: relErr }) => {
            if (relErr) {
              console.error("Related stories fetch error:", relErr.message);
            }
            setRelatedStories((related as Story[]) ?? []);
            setLoading(false);
          });
      })
      .catch((err: Error) => {
        console.error("Story detail unexpected error:", err.message);
        setError(err.message);
        setLoading(false);
      });
  }, [slug]);

  useEffect(() => {
    fetchStory();
  }, [fetchStory]);

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString(
      i18n.language === "ps"
        ? "ps-AF"
        : i18n.language === "da"
        ? "fa-AF"
        : "en-US",
      { year: "numeric", month: "long", day: "numeric" }
    );
  };

  if (loading) {
    return (
      <main className="pt-24 min-h-screen flex items-center justify-center bg-[#FAF8F5]">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#3B9EF5] border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-gray-400 text-sm mt-3">Loading story...</p>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="pt-24 min-h-screen flex items-center justify-center bg-[#FAF8F5]">
        <div className="text-center max-w-md">
          <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-red-50">
            <i className="ri-error-warning-line text-2xl text-red-400"></i>
          </div>
          <p className="text-gray-600 font-medium mb-1">Failed to load story</p>
          <p className="text-gray-400 text-sm mb-5">{error}</p>
          <button
            onClick={fetchStory}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#3B9EF5] text-[#1B2838] rounded-full font-semibold text-sm cursor-pointer hover:bg-[#2A8DE0] transition-colors whitespace-nowrap"
          >
            <i className="ri-refresh-line"></i> Retry
          </button>
        </div>
      </main>
    );
  }

  if (notFound || !story) {
    return (
      <main className="pt-24 min-h-screen flex items-center justify-center bg-[#FAF8F5]">
        <div className="text-center">
          <i className="ri-emotion-sad-line text-6xl text-gray-300 mb-4 block"></i>
          <h2
            className="text-2xl font-bold text-[#1B2838] mb-2"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Story Not Found
          </h2>
          <p className="text-gray-500 mb-6">
            The story you're looking for doesn't exist or has been removed.
          </p>
          <Link
            to="/stories"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#3B9EF5] text-[#1B2838] rounded-full text-sm font-bold cursor-pointer hover:bg-[#2A8DE0] transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            {t("storiesPage.backToStories")}
          </Link>
        </div>
      </main>
    );
  }

  const lang = i18n.language;
  const title = getLocalizedField(story, "title", lang);
  const content = getLocalizedField(story, "content", lang);
  const author = getLocalizedField(story, "author", lang);
  const authorRole = getLocalizedField(story, "author_role", lang);
  const readTime = getLocalizedField(story, "read_time", lang);

  return (
    <main className="pt-24">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-3 flex items-center gap-2 text-sm text-gray-500">
          <Link
            to="/"
            className="hover:text-[#3B9EF5] transition-colors cursor-pointer"
          >
            {t("projectDetail.home")}
          </Link>
          <i className="ri-arrow-right-s-line text-xs"></i>
          <Link
            to="/stories"
            className="hover:text-[#3B9EF5] transition-colors cursor-pointer"
          >
            {t("nav.stories")}
          </Link>
          <i className="ri-arrow-right-s-line text-xs"></i>
          <span className="text-gray-400 truncate">{title}</span>
        </div>
      </div>

      {/* Hero Image */}
      <div className="relative w-full h-[280px] sm:h-[380px] md:h-[480px] overflow-hidden">
        <img
          src={story.image}
          alt={title}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
      </div>

      {/* Content */}
      <article className="py-10 md:py-14 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          {/* Meta */}
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <span
              className={`text-xs font-semibold px-3 py-1 rounded-full ${
                categoryColorMap[story.category] || "bg-gray-100 text-gray-600"
              }`}
            >
              {t(categoryKeyMap[story.category])}
            </span>
            <span className="text-sm text-gray-400 flex items-center gap-1">
              <i className="ri-calendar-line"></i>
              {t("storiesPage.published")}: {formatDate(story.created_at)}
            </span>
            <span className="text-sm text-gray-400 flex items-center gap-1">
              <i className="ri-time-line"></i>
              {readTime}
            </span>
          </div>

          {/* Title */}
          <h1
            className="text-3xl md:text-4xl font-bold text-[#1B2838] mb-4 leading-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {title}
          </h1>

          {/* Author */}
          <div className="flex items-center gap-4 pb-8 mb-8 border-b border-gray-100">
            <div className="w-11 h-11 rounded-full bg-[#3B9EF5]/20 flex items-center justify-center text-[#3B9EF5] font-bold text-sm">
              {author.charAt(0)}
            </div>
            <div>
              <p className="font-semibold text-[#1B2838] text-sm">{author}</p>
              <p className="text-gray-500 text-xs">{authorRole}</p>
            </div>
          </div>

          {/* Body */}
          <div
            className="prose prose-base md:prose-lg max-w-none text-gray-700 leading-relaxed
              prose-h3:text-[#1B2838] prose-h3:font-bold prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
              prose-p:mb-5 prose-p:leading-relaxed
              prose-strong:text-[#1B2838]
              prose-ul:my-4 prose-li:mb-1.5 prose-li:text-gray-700
              [&_ul]:list-disc [&_ul]:pl-5"
            style={{ fontFamily: "'Inter', sans-serif" }}
            dangerouslySetInnerHTML={{ __html: content }}
          ></div>

          {/* Back Link */}
          <div className="mt-12 pt-8 border-t border-gray-100">
            <Link
              to="/stories"
              className="inline-flex items-center gap-2 text-[#3B9EF5] font-semibold text-sm hover:text-[#2A8DE0] transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              {t("storiesPage.backToStories")}
            </Link>
          </div>
        </div>
      </article>

      {/* Related Stories */}
      {relatedStories.length > 0 && (
        <section className="py-14 md:py-16 bg-[#FAF8F5]">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h3
              className="text-2xl font-bold text-[#1B2838] mb-8 text-center"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              More {t(categoryKeyMap[story.category])}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              {relatedStories.map((s) => (
                <Link
                  key={s.id}
                  to={`/stories/${s.slug}`}
                  className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-[#3B9EF5]/40 transition-all duration-300 group flex flex-col cursor-pointer"
                >
                  <div className="relative h-48 overflow-hidden flex-shrink-0">
                    <img
                      src={s.image}
                      alt={getLocalizedField(s, "title", lang)}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <h4 className="font-bold text-[#1B2838] text-base mb-2 leading-tight group-hover:text-[#3B9EF5] transition-colors line-clamp-2">
                      {getLocalizedField(s, "title", lang)}
                    </h4>
                    <p className="text-gray-500 text-sm leading-relaxed flex-1 line-clamp-2">
                      {getLocalizedField(s, "excerpt", lang)}
                    </p>
                    <span className="text-[#3B9EF5] text-sm font-semibold mt-3 flex items-center gap-1">
                      {t("storiesPage.readMore")}
                      <i className="ri-arrow-right-line text-xs"></i>
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}